/**
 * CapacityUtilisationChart Component
 * US-013D Phase 2: 14-day capacity utilisation visualisation
 *
 * Features:
 * - Stacked bar chart by resource type
 * - Colour coding: Red (>100%), Amber (90-100%), Green (60-90%), Grey (<60%)
 * - Hover shows resource details
 * - Click day to see detailed breakdown
 */

import { useState } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Cell
} from 'recharts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  AlertTriangle,
  Loader2,
  RefreshCw,
  Activity,
  Calendar
} from 'lucide-react'
import { cn } from '@/lib/utils'

// =============================================================================
// Colour Configuration
// =============================================================================

/**
 * Get colour based on utilisation percentage
 */
function getUtilisationColour(pct) {
  if (pct > 100) return '#ef4444'   // Red - over-capacity
  if (pct >= 90) return '#f59e0b'   // Amber - tight
  if (pct >= 60) return '#10b981'   // Green - normal
  return '#9ca3af'                   // Grey - underutilised
}

// Note: getStatusText can be used for accessibility labels in future
// function getStatusText(pct) {
//   if (pct > 100) return 'Over-capacity'
//   if (pct >= 90) return 'Tight'
//   if (pct >= 60) return 'Normal'
//   return 'Low'
// }

/**
 * Resource colours for stacking
 */
const RESOURCE_COLOURS = {
  heat_cycle_daily: '#F54A00',      // Odin primary
  fruiting_positions: '#009689',    // Odin secondary
  incubation_positions: '#6366f1',  // Indigo
  spawn_storage: '#8b5cf6'          // Purple
}

// =============================================================================
// Custom Tooltip
// =============================================================================

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null

  // Find the original day data
  const dayData = payload[0]?.payload

  return (
    <div className="bg-white border border-border rounded-lg p-3 shadow-lg min-w-[200px]">
      <p className="font-medium text-sm mb-2">{label}</p>

      {dayData?.hasConflict && (
        <div className="flex items-center gap-1 text-red-600 text-xs mb-2">
          <AlertTriangle className="h-3 w-3" />
          <span>Over-capacity detected</span>
        </div>
      )}

      <div className="space-y-2">
        {payload.map((entry, index) => {
          const resourceCode = entry.dataKey
          const usedKey = `${resourceCode}_used`
          const capacityKey = `${resourceCode}_capacity`

          const used = dayData?.[usedKey] || 0
          const capacity = dayData?.[capacityKey] || 0
          const pct = entry.value || 0

          return (
            <div key={index} className="flex items-center justify-between gap-4 text-xs">
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-sm"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-muted-foreground capitalize">
                  {resourceCode.replace(/_/g, ' ')}
                </span>
              </div>
              <div className="text-right">
                <span className="font-medium">{pct.toFixed(0)}%</span>
                <span className="text-muted-foreground ml-1">
                  ({used}/{capacity})
                </span>
              </div>
            </div>
          )
        })}
      </div>

      {dayData?.totalUtilisation !== undefined && (
        <div className="border-t border-border mt-2 pt-2">
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">Average Utilisation</span>
            <span className="font-medium">{dayData.totalUtilisation.toFixed(0)}%</span>
          </div>
        </div>
      )}
    </div>
  )
}

// =============================================================================
// Day Detail Panel
// =============================================================================

function DayDetailPanel({ date, resources, onClose }) {
  if (!date) return null

  return (
    <Card className="mt-4">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {new Date(date).toLocaleDateString('en-GB', {
                weekday: 'long',
                day: 'numeric',
                month: 'long'
              })}
            </CardTitle>
            <CardDescription>Capacity breakdown</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {resources.map((resource) => (
            <div
              key={resource.resource_id}
              className={cn(
                'p-3 rounded-lg border',
                resource.status === 'over_capacity' && 'border-red-300 bg-red-50',
                resource.status === 'tight' && 'border-amber-300 bg-amber-50',
                resource.status === 'available' && 'border-gray-200 bg-gray-50',
                resource.status === 'downtime' && 'border-purple-300 bg-purple-50'
              )}
            >
              <p className="font-medium text-sm truncate">{resource.resource_name}</p>
              <div className="mt-2 space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Used</span>
                  <span className="font-medium">{resource.total_used}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Capacity</span>
                  <span className="font-medium">{resource.capacity_value}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Available</span>
                  <span className="font-medium">{resource.available}</span>
                </div>
              </div>
              <div className="mt-2">
                <div
                  className="h-2 rounded-full bg-gray-200 overflow-hidden"
                >
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${Math.min(resource.utilisation_pct, 100)}%`,
                      backgroundColor: getUtilisationColour(resource.utilisation_pct)
                    }}
                  />
                </div>
                <p className="text-xs text-center mt-1 font-medium">
                  {resource.utilisation_pct.toFixed(0)}%
                </p>
              </div>

              {resource.allocations && resource.allocations.length > 0 && (
                <div className="mt-3 border-t border-border pt-2">
                  <p className="text-xs text-muted-foreground mb-1">Allocations:</p>
                  <div className="space-y-1">
                    {resource.allocations.slice(0, 3).map((alloc, idx) => (
                      <div key={idx} className="text-xs flex justify-between">
                        <span className="truncate">{alloc.entity_type}</span>
                        <span className="font-medium">{alloc.quantity}</span>
                      </div>
                    ))}
                    {resource.allocations.length > 3 && (
                      <p className="text-xs text-muted-foreground">
                        +{resource.allocations.length - 3} more
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export default function CapacityUtilisationChart({
  chartData,
  byDate,
  resources,
  // conflicts prop available for future conflict highlighting features
  conflicts: _conflicts,
  loading,
  error,
  onRefresh,
  onDateSelect
}) {
  // Suppress unused variable warning - conflicts will be used for highlighting
  void _conflicts
  const [selectedDate, setSelectedDate] = useState(null)

  // Get resource codes for bars
  const resourceCodes = resources?.map(r => r.code) || []

  // Handle bar click
  const handleBarClick = (data) => {
    if (data && data.activePayload && data.activePayload[0]) {
      const date = data.activePayload[0].payload.date
      const dayData = byDate?.find(d => d.date === date)

      setSelectedDate(date)

      if (onDateSelect) {
        onDateSelect(date, dayData)
      }
    }
  }

  // Get selected day resources
  const selectedDayResources = selectedDate
    ? byDate?.find(d => d.date === selectedDate)?.resources || []
    : []

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading capacity data...</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    )
  }

  if (!chartData || chartData.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center text-muted-foreground">
            <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No capacity data available</p>
            <p className="text-sm mt-1">Generate a production plan to see capacity utilisation</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Count conflict days
  const conflictDays = chartData.filter(d => d.hasConflict).length

  return (
    <div className="space-y-4">
      {/* Header with legend */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            Capacity Utilisation (14 Days)
          </h3>
          <p className="text-sm text-muted-foreground">
            Click a day to see detailed breakdown
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Legend */}
          <div className="hidden md:flex items-center gap-3 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-sm bg-red-500" />
              <span>&gt;100%</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-sm bg-amber-500" />
              <span>90-100%</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-sm bg-emerald-500" />
              <span>60-90%</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-sm bg-gray-400" />
              <span>&lt;60%</span>
            </div>
          </div>

          {onRefresh && (
            <Button variant="outline" size="sm" onClick={onRefresh}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Conflict alert */}
      {conflictDays > 0 && (
        <Alert className="border-red-200 bg-red-50">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertTitle className="text-red-800">Capacity Conflicts Detected</AlertTitle>
          <AlertDescription className="text-red-700">
            {conflictDays} day{conflictDays !== 1 ? 's' : ''} have capacity conflicts that need resolution.
            Review the red bars below and click for details.
          </AlertDescription>
        </Alert>
      )}

      {/* Chart */}
      <Card>
        <CardContent className="pt-6">
          <ResponsiveContainer width="100%" height={350}>
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              onClick={handleBarClick}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis
                dataKey="dateLabel"
                axisLine={false}
                tickLine={false}
                tick={{
                  fontSize: 11,
                  fill: 'var(--muted-foreground)',
                  fontFamily: 'Geist, -apple-system, BlinkMacSystemFont, sans-serif'
                }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis
                domain={[0, 'auto']}
                axisLine={false}
                tickLine={false}
                tick={{
                  fontSize: 12,
                  fill: 'var(--muted-foreground)',
                  fontFamily: 'Geist, -apple-system, BlinkMacSystemFont, sans-serif'
                }}
                tickFormatter={(value) => `${value}%`}
                label={{
                  value: 'Utilisation %',
                  angle: -90,
                  position: 'insideLeft',
                  style: { textAnchor: 'middle', fontSize: '12px', fill: 'var(--muted-foreground)' }
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{
                  paddingTop: '20px',
                  fontSize: '12px',
                  fontFamily: 'Geist, -apple-system, BlinkMacSystemFont, sans-serif'
                }}
                formatter={(value) => value.replace(/_/g, ' ')}
              />

              {/* 100% reference line */}
              <ReferenceLine
                y={100}
                stroke="#ef4444"
                strokeDasharray="5 5"
                label={{
                  value: '100%',
                  position: 'right',
                  fill: '#ef4444',
                  fontSize: 10
                }}
              />

              {/* 90% warning line */}
              <ReferenceLine
                y={90}
                stroke="#f59e0b"
                strokeDasharray="3 3"
                strokeOpacity={0.5}
              />

              {/* Bars for each resource */}
              {resourceCodes.map((code, index) => (
                <Bar
                  key={code}
                  dataKey={code}
                  name={code}
                  stackId="capacity"
                  fill={RESOURCE_COLOURS[code] || '#6b7280'}
                  radius={index === resourceCodes.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                  cursor="pointer"
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Day detail panel */}
      {selectedDate && (
        <DayDetailPanel
          date={selectedDate}
          resources={selectedDayResources}
          onClose={() => setSelectedDate(null)}
        />
      )}
    </div>
  )
}
